// interiorQuotationPdf.js - "Download" action for a row on the quotations list screen
// (InteriorQuation.js). There's no visible preview open at that point, so this renders the
// same InteriorQuotationDocument used by PreviewInteriorQuatation.js into an off-screen
// container, captures it, and cleans up - the list page never shows the intermediate DOM.
import React from 'react';
import { createRoot } from 'react-dom/client';
import { getTenantLogoDataUrl, isTenantLogoDataUrl } from '../../services/tenantLogoApi';
import { computeQuotationTotals } from './interiorQuotationModel';
import {
  exportNodeToPdf,
  InteriorQuotationDocument,
  resetPdfExportImages,
  waitForPdfImageCells,
} from './PreviewInteriorQuatation';

export const downloadInteriorQuotationPdf = async (record) => {
  if (!record) {
    throw new Error('No quotation record to export.');
  }

  const totals = computeQuotationTotals({
    sections: record.sections || [],
    designFeePercent: record.designFeePercent,
    gstPercent: record.gstPercent,
    paymentSchedule: record.paymentSchedule || [],
  });

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '0';
  container.style.left = '0';
  container.style.opacity = '0.01';
  container.style.pointerEvents = 'none';
  container.style.zIndex = '-1';
  document.body.appendChild(container);

  const root = createRoot(container);
  const contentRef = React.createRef();

  try {
    const fetchedLogo = await getTenantLogoDataUrl().catch(() => null);
    const logoUrl = isTenantLogoDataUrl(fetchedLogo) ? fetchedLogo : undefined;

    resetPdfExportImages();
    await new Promise((resolve) => {
      root.render(
        React.createElement(InteriorQuotationDocument, {
          ref: contentRef,
          siteId: record.siteId,
          header: record.header,
          sections: record.sections || [],
          designFeePercent: record.designFeePercent,
          gstPercent: record.gstPercent,
          templates: record.templates,
          invoiceLabel: record.invoiceNumber ? `Invoice ${record.invoiceNumber}` : '',
          documentVersion: record.version
            ? String(record.version).startsWith('v')
              ? String(record.version)
              : `v${record.version}`
            : record.majorVersion != null
              ? `v${record.majorVersion}.${record.minorVersion ?? 0}`
              : '',
          forExport: true,
          logoUrl,
          ...totals,
        })
      );
      // Two rAFs so the just-mounted table (rowSpans, colgroups) has a committed layout
      // before html2canvas measures it.
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

    if (!contentRef.current) {
      throw new Error('Unable to render quotation document.');
    }

    await waitForPdfImageCells(contentRef.current);

    const clientSlug = (record.header?.client || 'quotation').trim().replace(/\s+/g, '-');
    await exportNodeToPdf(contentRef.current, `${clientSlug}-interior-quotation.pdf`);
  } finally {
    root.unmount();
    container.remove();
    resetPdfExportImages();
  }
};
